'use client';

import React, { useState } from 'react';
import { Upload, Camera, CheckCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface OCRUploadPanelProps {
  onClose: () => void;
}

interface ExtractedData {
  utr: string;
  amount: string;
  date: string;
  time: string;
  reference: string;
  isDuplicate: boolean;
}

export default function OCRUploadPanel({ onClose }: OCRUploadPanelProps) {
  const [uploadState, setUploadState] = useState<'idle' | 'uploading' | 'extracting' | 'done'>('idle');
  const [extracted, setExtracted] = useState<ExtractedData | null>(null);
  const [lotLink, setLotLink] = useState('');

  const simulateOCR = async () => {
    setUploadState('uploading');
    await new Promise((r) => setTimeout(r, 800));
    setUploadState('extracting');
    await new Promise((r) => setTimeout(r, 1200));
    // Backend integration point: POST /api/ocr/extract with image file → returns UTR, amount, date, time
    setExtracted({
      utr: 'UTR512847391',
      amount: '22500',
      date: '18/06/2026',
      time: '10:34 AM',
      reference: 'Payment for Lot #300 — Onion',
      isDuplicate: false,
    });
    setUploadState('done');
  };

  const handleConfirm = async () => {
    if (!lotLink) {
      toast.error('Please select a lot to link this payment');
      return;
    }
    // Backend integration point: POST /api/payments with extracted data + lot_id
    toast.success('Payment recorded — UTR ' + extracted?.utr);
    onClose();
  };

  return (
    <div className="space-y-4">
      {/* Upload Zone */}
      {uploadState === 'idle' && (
        <div
          onClick={simulateOCR}
          className="border-2 border-dashed border-border rounded-2xl p-8 flex flex-col items-center gap-4 cursor-pointer hover:border-primary/40 transition-colors group"
        >
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
            <Upload size={28} className="text-primary" />
          </div>
          <div className="text-center">
            <p className="text-sm font-semibold text-foreground">Upload UPI Screenshot</p>
            <p className="text-xs text-muted-foreground mt-1">
              Tap to upload or take photo — UTR, amount, date, time will be auto-extracted
            </p>
          </div>
          <div className="flex gap-3">
            <button type="button" className="btn-secondary text-xs py-2 px-4">
              <Upload size={13} />
              Upload File
            </button>
            <button type="button" className="btn-secondary text-xs py-2 px-4">
              <Camera size={13} />
              Take Photo
            </button>
          </div>
        </div>
      )}

      {/* Loading states */}
      {(uploadState === 'uploading' || uploadState === 'extracting') && (
        <div className="flex flex-col items-center gap-4 py-8">
          <Loader2 size={32} className="text-primary animate-spin" />
          <div className="text-center">
            <p className="text-sm font-semibold text-foreground">
              {uploadState === 'uploading' ? 'Uploading screenshot...' : 'Extracting payment details...'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {uploadState === 'extracting' ? 'Reading UTR, amount, date and time from image' : 'Please wait'}
            </p>
          </div>
          <div className="w-48 bg-muted rounded-full h-1.5">
            <div
              className="h-1.5 bg-primary rounded-full transition-all duration-500"
              style={{ width: uploadState === 'uploading' ? '40%' : '85%' }}
            />
          </div>
        </div>
      )}

      {/* Extracted Data */}
      {uploadState === 'done' && extracted && (
        <div className="space-y-4">
          {/* Duplicate Warning */}
          {extracted.isDuplicate && (
            <div className="flex items-center gap-3 bg-danger/10 border border-danger/30 rounded-xl p-3">
              <AlertTriangle size={16} className="text-danger" />
              <div>
                <p className="text-sm font-semibold text-danger">Duplicate UTR Detected</p>
                <p className="text-xs text-muted-foreground">This UTR number was already recorded. Do not save again.</p>
              </div>
            </div>
          )}

          {!extracted.isDuplicate && (
            <div className="flex items-center gap-2 bg-accent/10 border border-accent/20 rounded-xl p-3">
              <CheckCircle size={16} className="text-accent" />
              <p className="text-sm font-semibold text-accent">Extraction Successful — New UTR</p>
            </div>
          )}

          {/* Extracted fields */}
          <div className="bg-muted/30 rounded-xl p-4 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Extracted Details</p>
            {[
              { label: 'UTR Number', value: extracted.utr, mono: true },
              { label: 'Amount', value: `₹${Number(extracted.amount).toLocaleString('en-IN')}`, mono: true },
              { label: 'Date', value: extracted.date, mono: false },
              { label: 'Time', value: extracted.time, mono: false },
              { label: 'Reference', value: extracted.reference, mono: false },
            ].map((field) => (
              <div key={`extracted-${field.label}`} className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{field.label}</span>
                <span className={`text-sm font-semibold text-foreground ${field.mono ? 'font-mono' : ''}`}>
                  {field.value}
                </span>
              </div>
            ))}
          </div>

          {/* Link to lot */}
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1.5">
              Link to Lot <span className="text-danger">*</span>
            </label>
            <select
              value={lotLink}
              onChange={(e) => setLotLink(e.target.value)}
              className="input-field"
            >
              <option value="">Select lot</option>
              {['300 — Onion', '470 — Potato', '410 — Garlic', '185 — Onion', '211 — Tomato'].map((opt) => (
                <option key={`ocr-lot-${opt}`} value={opt.split(' ')[0]}>{`Lot #${opt}`}</option>
              ))}
            </select>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button onClick={onClose} className="btn-secondary flex-1">
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={extracted.isDuplicate}
              className="btn-primary flex-1"
            >
              {extracted.isDuplicate ? 'Duplicate — Cannot Save' : 'Save Payment'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}